import React, { useContext, useMemo, useRef, useState } from 'react';
import { useEffect } from 'react';
import { useAppDispatch, useAppSelector } from '../store';
import { BarLoader } from 'react-spinners';
import { getGames } from '../reducers/gamesReducer';
import { fetchGroups, createGroup } from '../reducers/groupsReducer';
import { setParams } from '../reducers/paramsReducer';
import GamesAPI from '../api/GamesAPI';
import { GameModel } from '../types/GamesModel';
import { Alert, Button, Dropdown, Form, FormLabel, Modal, Nav } from 'react-bootstrap';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
  faCamera,
  faClipboard,
  faCloudArrowDown,
  faLock,
  faGlobe,
  faXmark,
  faLayerGroup,
  faLink,
} from '@fortawesome/free-solid-svg-icons';
import { setShowImportGames } from '../reducers/uiReducer';
import { Platform } from '../types/Platform';
import { GamesRequestParams } from '../types/GamesRequestParams';
import { GroupKind, GroupVisibility } from '../types/GroupModel';
import { DataLoader } from '../utils/DataLoader';
import ImportAPI from '../api/ImportAPI';
import { analytics } from '../utils/analytics';
import { clientLog } from '../utils/clientLog';
import { setHandle } from '../reducers/settingsReducer';
import {
  addSearchName,
  fetchSearchNames,
  removeSearchName,
} from '../reducers/searchNamesReducer';
import { platformName, readRecentUsernames, addRecentUsername } from '../utils/userSettings';
import AppContext from '../context/AppContext';
import ConnectedAccounts from './ConnectedAccounts';

type ImportTab = 'platform' | 'pgn' | 'photo' | 'accounts';

// Mirrors ImportController.FirstImportWindow, and the UI states it, so keep them in sync.
const FIRST_IMPORT_DAYS = 30;

export default function GamesImportGamesModal(): React.ReactNode {
  const dispatch = useAppDispatch();
  const { isAuthenticated, openLogin } = useContext(AppContext);
  const { params } = useAppSelector((state) => state.paramsReducer);
  const { showImportGames } = useAppSelector((state) => state.uiReducer);
  const { games } = useAppSelector((state) => state.gamesReducer);
  const { handles } = useAppSelector((state) => state.settingsReducer);
  const { groups } = useAppSelector((state) => state.groupsReducer);
  // The signed-in player's saved handles, the same list the Player filter manages.
  const { names: savedNames, loaded: savedNamesLoaded } = useAppSelector(
    (state) => state.searchNamesReducer,
  );
  const { username: pUsername, platform: pPlatform } = params;

  const [tab, setTab] = useState<ImportTab>('platform');
  const [pgn, setPgn] = useState('');
  const [username, setUsername] = useState<string>();
  const [platform, setPlatform] = useState<Platform | undefined>(Platform.ChessCom);
  // Optional cursor (yyyy-mm-dd) to re-import older games from a date. Blank = incremental.
  const [fromDate, setFromDate] = useState<string>();
  const [errorMessage, setErrorMessage] = useState<string>();
  const [importing, setImporting] = useState(false);
  // An import finished with no games in the window: stay open and offer a longer time frame.
  const [noGamesFound, setNoGamesFound] = useState(false);
  const [latestGame, setLatestGame] = useState<DataLoader<GameModel>>();
  // Dropdown of previously entered handles, like the Player filter (GamesFiltersUser).
  const [handleOpen, setHandleOpen] = useState(false);
  const handleRef = useRef<HTMLDivElement>(null);

  // Groups the user may add to (My Space, groups they own, Home for the webmaster).
  const addableGroups = useMemo(() => groups.filter((g) => g.CanAdd), [groups]);
  const [selectedGroupId, setSelectedGroupId] = useState<number | null>(null);

  // Inline "new group" form state.
  const [creatingGroup, setCreatingGroup] = useState(false);
  const [newGroupName, setNewGroupName] = useState('');
  const [newGroupPublic, setNewGroupPublic] = useState(false);

  // Default target: the group being viewed if the user can add to it, else My Space. Re-seeded on
  // each open. A null params.group is the Home view, so resolve it to the Home group explicitly.
  useEffect(() => {
    if (!showImportGames) {
      return;
    }
    const active =
      params.group != null && params.group !== ''
        ? addableGroups.find((g) => g.Token === params.group)
        : addableGroups.find((g) => g.Kind === GroupKind.Home);
    const mySpace = addableGroups.find((g) => g.Kind === GroupKind.Personal && g.IsOwner);
    setSelectedGroupId(active?.Id ?? mySpace?.Id ?? addableGroups[0]?.Id ?? null);
    setCreatingGroup(false);
    setNoGamesFound(false);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [showImportGames]);

  // Load the saved handles once for a signed-in visitor (the endpoint 401s when signed out).
  useEffect(() => {
    if (showImportGames && isAuthenticated && !savedNamesLoaded) {
      dispatch(fetchSearchNames());
    }
  }, [showImportGames, isAuthenticated, savedNamesLoaded, dispatch]);

  // Suggestions: the whole list when the field is empty or shows a saved name, else matches.
  const handleQuery = (username ?? '').trim().toLowerCase();
  const handleSuggestions = useMemo(() => {
    const showAll = handleQuery === '' || savedNames.some((n) => n.toLowerCase() === handleQuery);
    return showAll ? savedNames : savedNames.filter((n) => n.toLowerCase().includes(handleQuery));
  }, [savedNames, handleQuery]);

  // Close the dropdown on any outside click, only while it's open.
  useEffect(() => {
    if (!handleOpen) {
      return;
    }
    const onDown = (e: MouseEvent) => {
      if (handleRef.current && !handleRef.current.contains(e.target as Node)) {
        setHandleOpen(false);
      }
    };
    document.addEventListener('mousedown', onDown);
    return () => document.removeEventListener('mousedown', onDown);
  }, [handleOpen]);

  useEffect(() => {
    try {
      setLatestGame({ loading: true });
      if (username && username != '' && platform != null) {
        GamesAPI.getLatestGame(username, platform).then((data) => {
          setLatestGame({ data: data, loading: false });
        });
      }
    } catch (error) {
      setLatestGame({ error: error, loading: false });
    }
  }, [platform, username]);

  // Seed the platform from the games filter on open only; `platform` is edited below.
  useEffect(() => {
    if (showImportGames && pPlatform != null) {
      setPlatform(pPlatform);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [showImportGames]);

  // Handles typed or imported before, in localStorage, so a second account survives even though the
  // saved-handle map keeps one name per platform.
  const [recentUsernames, setRecentUsernames] = useState<string[]>(() => readRecentUsernames());

  // Username suggestions: recent and saved handles first, then names in the current list by frequency.
  // De-duped case-insensitively.
  const users = useMemo(() => {
    const freq = new Map<string, number>();
    for (const g of games?.List ?? []) {
      for (const n of [g.White, g.Black]) {
        if (n) {
          freq.set(n, (freq.get(n) ?? 0) + 1);
        }
      }
    }
    const byFrequency = [...freq.entries()].sort((a, b) => b[1] - a[1]).map(([n]) => n);
    const savedHandles = Object.values(handles).filter((v): v is string => typeof v === 'string');
    const ordered = [...recentUsernames, ...savedHandles, ...byFrequency];
    const seen = new Set<string>();
    const unique: string[] = [];
    for (const name of ordered) {
      const trimmed = name.trim();
      const key = trimmed.toLowerCase();
      if (trimmed === '' || seen.has(key)) {
        continue;
      }
      seen.add(key);
      unique.push(trimmed);
    }
    return unique;
  }, [games?.List, recentUsernames, handles]);

  // Seed the box so a player never types their handle twice: the filtered player first (they probably
  // searched, found nothing, and came here to import), then the saved handle for this platform, then the
  // most frequent name in the list. Re-runs when the platform changes or data arrives late.
  useEffect(() => {
    if (!showImportGames) {
      return;
    }
    const name = platform != null ? platformName(platform) : null;
    const saved = name != null ? handles[name] : undefined;
    setUsername(pUsername ?? saved ?? users[0] ?? '');
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [showImportGames, platform, handles, users]);

  const platformLabel = useMemo(
    () =>
      platform == Platform.ChessCom ? 'Chess.com' : platform == Platform.Lichess ? 'Lichess' : null,
    [platform],
  );

  // A group id's URL token.
  const groupToken = (groupId: number | null) =>
    groupId != null ? (groups.find((g) => g.Id === groupId)?.Token ?? null) : null;

  // A view scoped to the games just imported: this handle and group, every other filter cleared so a
  // stray filter can't hide them. Fully specified because setParams merges over the current params.
  const viewImportedParams = (uname: string | null, group: string | null): GamesRequestParams => ({
    username: uname,
    group,
    color: null,
    result: null,
    platform: null,
    opening: null,
    timeControl: null,
    from: null,
    to: null,
    pgn: null,
    onlyStarred: null,
    favoriteMoves: null,
    hasComments: null,
    commentedByMe: null,
    page: 0,
  });

  // Show the imported games and close. Navigating also clears ?import=1 so the modal doesn't reopen.
  // `token` is where the server put the games (see StartImportResult).
  const afterImport = (uname: string | null, token: string | null) => {
    const next = viewImportedParams(uname, token);
    dispatch(setParams(next));
    dispatch(getGames(next));
    dispatch(fetchGroups());
    dispatch(setShowImportGames(false));
  };

  const createNewGroup = async () => {
    const name = newGroupName.trim();
    if (name === '') {
      return;
    }
    try {
      const group = await dispatch(
        createGroup({
          name,
          visibility: newGroupPublic ? GroupVisibility.Public : GroupVisibility.Private,
        }),
      ).unwrap();
      setSelectedGroupId(group.Id);
      setCreatingGroup(false);
      setNewGroupName('');
      setNewGroupPublic(false);
    } catch (error) {
      setErrorMessage(error.message ?? 'Could not create the group.');
    }
  };

  // `fromOverride` lets "try a longer time frame" re-run without waiting for the fromDate state write.
  const onClick = (fromOverride?: string) => {
    if (!(username && username != '' && platform != null)) {
      setErrorMessage('Enter your username and pick a platform.');
      return;
    }
    const uname = username;
    const plat = platform;
    const targetGroupId = selectedGroupId;
    const from = fromOverride ?? fromDate;
    setImporting(true);
    setErrorMessage(undefined);
    setNoGamesFound(false);
    // Remember the handle for suggestions right away.
    setRecentUsernames(addRecentUsername(uname));
    analytics.track('import_start', { platform: Platform[plat] });
    clientLog.info('import_start', {
      handle: uname,
      platform: Platform[plat],
      from: from || null,
      groupId: targetGroupId,
    });
    // The POST probes the handle up front and enqueues the job; a bad handle or platform rejects fast.
    ImportAPI.startImportPlatform(uname, plat, targetGroupId, from || undefined)
      .then(async ({ jobId, importedNow, groupToken: landedToken }) => {
        clientLog.info('import_enqueued', {
          handle: uname, platform: Platform[plat], jobId, importedNow, groupToken: landedToken,
        });
        // Validated, so this is the player's handle on this platform: save it and add it to the list.
        const name = platformName(plat);
        if (name != null) {
          dispatch(setHandle({ platform: name, username: uname }));
        }
        dispatch(addSearchName(uname));
        // The newest games are imported inline, so if any landed show them now. Only wait on the job when
        // nothing came back inline; that also keeps import instant on a dev box with no worker.
        let importError: string | null = null;
        if (importedNow === 0) {
          try {
            await ImportAPI.waitForImport(jobId);
            clientLog.info('import_job_done', { jobId, handle: uname, platform: Platform[plat] });
          } catch (e) {
            importError = (e as Error).message;
            clientLog.warn('import_job_failed', { jobId, handle: uname, error: importError });
          }
        }
        // Scope to the group the server actually used. With none selected it's My Space, which the client
        // can't name; null would query Home and report "no games found".
        const token = landedToken ?? groupToken(targetGroupId);
        // Verify with the same query as the post-import view. A completed job with downloads but Total 0
        // means this query doesn't match what landed, so log it.
        const total = await GamesAPI.getGames(viewImportedParams(uname, token))
          .then((r) => r.Total ?? r.List.length)
          .catch((e) => {
            clientLog.error('import_verify_query_failed', { handle: uname, groupToken: token, error: (e as Error).message });
            return 0;
          });
        const found = total > 0;
        clientLog.info('import_verify', {
          jobId,
          handle: uname,
          platform: Platform[plat],
          groupToken: token,
          total,
          found,
          importError,
        });
        dispatch(fetchGroups());
        setImporting(false);
        if (found) {
          analytics.track('import_result', { platform: Platform[plat], found: 'yes' });
          afterImport(uname, token);
        } else if (importError != null) {
          setErrorMessage(importError);
        } else {
          analytics.track('import_result', { platform: Platform[plat], found: 'none' });
          clientLog.warn('import_result_none', {
            handle: uname,
            platform: Platform[plat],
            groupToken: token,
            from: from || null,
          });
          setNoGamesFound(true);
        }
      })
      .catch((error: Error) => {
        // Rejected at the POST: the probe said no, or the request failed.
        clientLog.warn('import_rejected', {
          handle: uname,
          platform: Platform[plat],
          from: from || null,
          error: error.message,
        });
        setErrorMessage(error.message);
        setImporting(false);
      });
  };

  // "No games in that window": reach back a full year.
  const searchLastYear = () => {
    const d = new Date();
    d.setFullYear(d.getFullYear() - 1);
    const iso = d.toISOString().slice(0, 10);
    setFromDate(iso);
    onClick(iso);
  };

  const onPgnClick = async () => {
    if (pgn && pgn !== '') {
      try {
        setErrorMessage(undefined);
        analytics.track('import_start', { platform: 'pgn' });
        const { groupToken: landedToken } = await ImportAPI.startImportPgn(pgn, selectedGroupId);
        // Pasted games can be anyone's, so show the target group without a handle filter.
        afterImport(null, landedToken ?? groupToken(selectedGroupId));
      } catch (error) {
        setErrorMessage(error.message);
      }
    } else {
      setErrorMessage('Paste a PGN to import.');
    }
  };

  const onSelectTab = (next: ImportTab) => {
    setTab(next);
    setErrorMessage(undefined);
    if (next === 'photo') {
      analytics.track('import_photo_interest');
    }
  };

  // The target group, on the platform and PGN tabs. Defaults to My Space; a new group can be created
  // inline.
  const groupTarget =
    tab !== 'photo' && tab !== 'accounts' ? (
      <div className="mb-3 cm-import-group">
        <FormLabel>Add to</FormLabel>
        {!creatingGroup ? (
          <div className="cm-import-group-row">
            <Form.Select
              value={selectedGroupId ?? ''}
              onChange={(e) => setSelectedGroupId(e.target.value ? Number(e.target.value) : null)}
            >
              {addableGroups.map((g) => (
                <option key={g.Id} value={g.Id}>
                  {g.Name}
                  {g.Kind === GroupKind.Home
                    ? ' (home page)'
                    : g.Visibility === GroupVisibility.Public
                      ? ' (public)'
                      : ' (private)'}
                </option>
              ))}
            </Form.Select>
            <Button
              variant="outline-secondary"
              className="cm-import-newgroup-btn"
              onClick={() => setCreatingGroup(true)}
            >
              <FontAwesomeIcon icon={faLayerGroup} className="me-2" />
              Add to group
            </Button>
          </div>
        ) : (
          <div className="cm-import-newgroup">
            <Form.Control
              placeholder="Group name (e.g. Caruana's games, My French repertoire)"
              value={newGroupName}
              onChange={(e) => setNewGroupName(e.target.value)}
              autoFocus
            />
            <Form.Check
              type="switch"
              id="cm-newgroup-public"
              className="mt-2"
              label={
                <span>
                  <FontAwesomeIcon icon={newGroupPublic ? faGlobe : faLock} className="me-2" />
                  {newGroupPublic ? 'Public. Anyone can see it' : 'Private. Only you'}
                </span>
              }
              checked={newGroupPublic}
              onChange={(e) => setNewGroupPublic(e.target.checked)}
            />
            <div className="cm-import-newgroup-actions mt-2">
              <Button size="sm" onClick={createNewGroup} disabled={newGroupName.trim() === ''}>
                Create group
              </Button>
              <Button size="sm" variant="link" onClick={() => setCreatingGroup(false)}>
                Cancel
              </Button>
            </div>
          </div>
        )}
      </div>
    ) : null;

  if (!showImportGames) {
    return null;
  }

  // Adding games needs an account; signed-out visitors get a login prompt.
  if (!isAuthenticated) {
    return (
      <Modal show={showImportGames} onHide={() => dispatch(setShowImportGames(false))}>
        <Modal.Header closeButton>
          <Modal.Title>Add games</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <p className="cm-import-intro">
            Sign in to add your games. They go into your own private space by default. Replayable,
            searchable, and yours. And you can group them however you like.
          </p>
          <div className="cm-import-actions">
            <Button
              variant="primary"
              onClick={() => {
                dispatch(setShowImportGames(false));
                openLogin?.();
              }}
            >
              Sign in
            </Button>
          </div>
        </Modal.Body>
      </Modal>
    );
  }

  return (
    <Modal show={showImportGames} onHide={() => dispatch(setShowImportGames(false))}>
      <Modal.Header closeButton>
        <Modal.Title>Add games</Modal.Title>
      </Modal.Header>
      <Modal.Body>
        <p className="cm-import-intro">
          Add your games once and they stay here. Replayable, searchable, and yours even if you
          change platforms. Pull them straight from your account, paste a PGN, or start from a photo
          of an annotation sheet.
        </p>

        <Nav
          variant="tabs"
          activeKey={tab}
          className="mb-3"
          onSelect={(key) => onSelectTab((key ?? 'platform') as ImportTab)}
        >
          <Nav.Item>
            <Nav.Link eventKey="platform">
              <FontAwesomeIcon icon={faCloudArrowDown} className="me-2" />
              From platform
            </Nav.Link>
          </Nav.Item>
          <Nav.Item>
            <Nav.Link eventKey="pgn">
              <FontAwesomeIcon icon={faClipboard} className="me-2" />
              Paste PGN
            </Nav.Link>
          </Nav.Item>
          <Nav.Item>
            <Nav.Link eventKey="photo">
              <FontAwesomeIcon icon={faCamera} className="me-2" />
              Take a picture
            </Nav.Link>
          </Nav.Item>
          <Nav.Item>
            <Nav.Link eventKey="accounts">
              <FontAwesomeIcon icon={faLink} className="me-2" />
              Connected accounts
            </Nav.Link>
          </Nav.Item>
        </Nav>

        {errorMessage != null ? (
          <Alert variant="danger" dismissible onClose={() => setErrorMessage(undefined)}>
            {errorMessage}
          </Alert>
        ) : null}

        {groupTarget}

        {tab === 'platform' ? (
          <>
            <p className="cm-import-help">
              Enter your account name and we&apos;ll fetch your games from the last{' '}
              {FIRST_IMPORT_DAYS} days. Import again later and we pick up where we left off, so
              nothing you&apos;ve played since is missed.
            </p>

            <div className="mb-3">
              <FormLabel htmlFor="cm-import-handle">Chess handle</FormLabel>
              <div className="cm-player-filter" style={{ position: 'relative' }} ref={handleRef}>
                <Form.Control
                  id="cm-import-handle"
                  name="chess"
                  value={username ?? ''}
                  placeholder="your chess account name"
                  onChange={(event) => {
                    setUsername(event.target.value);
                    setNoGamesFound(false);
                    setHandleOpen(true);
                  }}
                  onFocus={() => setHandleOpen(true)}
                  // A chess handle, not a login: iOS/Safari ignores autocomplete="off" when it reads a field as a
                  // sign-in username, so "username" stays out of the label and id. data-*-ignore covers desktop
                  // password managers.
                  autoComplete="off"
                  autoCorrect="off"
                  autoCapitalize="none"
                  spellCheck={false}
                  data-1p-ignore
                  data-lpignore="true"
                  role="combobox"
                  aria-expanded={handleOpen && handleSuggestions.length > 0}
                  aria-autocomplete="list"
                ></Form.Control>
                {handleOpen && handleSuggestions.length > 0 ? (
                  <ul className="cm-player-menu" role="listbox">
                    {handleSuggestions.map((name) => (
                      <li
                        key={name}
                        role="option"
                        aria-selected={name.toLowerCase() === handleQuery}
                        className="cm-player-option"
                        // preventDefault keeps focus on the input so it doesn't blur before the click.
                        onMouseDown={(e) => e.preventDefault()}
                        onClick={() => {
                          setUsername(name);
                          setNoGamesFound(false);
                          setHandleOpen(false);
                        }}
                      >
                        <span className="cm-player-option-name">{name}</span>
                        <button
                          type="button"
                          className="cm-player-option-remove"
                          title={`Remove ${name}`}
                          aria-label={`Remove ${name}`}
                          onMouseDown={(e) => e.preventDefault()}
                          onClick={(e) => {
                            e.stopPropagation();
                            dispatch(removeSearchName(name));
                          }}
                        >
                          <FontAwesomeIcon icon={faXmark} />
                        </button>
                      </li>
                    ))}
                  </ul>
                ) : null}
              </div>
            </div>
            <div className="mb-2">
              <FormLabel>Platform</FormLabel>
              <Dropdown>
                <Dropdown.Toggle variant="secondary">
                  {platformLabel ?? 'Select a platform'}
                </Dropdown.Toggle>
                <Dropdown.Menu>
                  <Dropdown.Item
                    active={platform == Platform.ChessCom}
                    onClick={() => {
                      setPlatform(Platform.ChessCom);
                    }}
                  >
                    Chess.com
                  </Dropdown.Item>
                  <Dropdown.Item
                    active={platform == Platform.Lichess}
                    onClick={() => {
                      setPlatform(Platform.Lichess);
                    }}
                  >
                    Lichess
                  </Dropdown.Item>
                </Dropdown.Menu>
              </Dropdown>
            </div>
            <div className="mb-3">
              <FormLabel htmlFor="cm-import-from">Re-import from (optional)</FormLabel>
              <Form.Control
                id="cm-import-from"
                type="date"
                value={fromDate ?? ''}
                max={new Date().toISOString().slice(0, 10)}
                onChange={(event) => {
                  setFromDate(event.target.value || undefined);
                  setNoGamesFound(false);
                }}
              />
              <Form.Text className="text-muted">
                Leave blank to continue where you left off. Pick a date to pull older games too:
                anything already here is skipped.
              </Form.Text>
              {!fromDate && latestGame?.data != null ? (
                <div className="mt-2">
                  <strong>
                    Your last game here was downloaded on{' '}
                    {new Date(latestGame.data.Date).toDateString()}. This import continues from
                    there.
                  </strong>
                </div>
              ) : null}
            </div>

            {noGamesFound ? (
              <Alert variant="warning" className="cm-import-nogames">
                <p className="mb-2">
                  No games found for <strong>{username}</strong> on {platformLabel}
                  {fromDate
                    ? ` since ${new Date(fromDate).toDateString()}`
                    : ` in the last ${FIRST_IMPORT_DAYS} days`}
                  . Double-check the handle and platform, or reach further back.
                </p>
                <Button variant="outline-secondary" size="sm" onClick={searchLastYear} disabled={importing}>
                  Search the last 12 months
                </Button>
              </Alert>
            ) : null}

            <div className="cm-import-actions">
              <Button variant="primary" onClick={() => onClick()} disabled={importing}>
                {importing ? 'Adding…' : 'Add games'}
              </Button>
            </div>
          </>
        ) : null}

        {tab === 'accounts' ? <ConnectedAccounts /> : null}

        {tab === 'pgn' ? (
          <>
            <p className="cm-import-help">
              Paste a PGN from anywhere. A tournament export, an email, another site. One game or
              several.
            </p>
            <Form.Control
              as="textarea"
              className="cm-import-pgn"
              placeholder="[Event &quot;…&quot;]&#10;1. e4 e5 2. Nf3 …"
              value={pgn}
              onChange={(e) => setPgn(e.target.value)}
            />
            <div className="cm-import-actions">
              <Button variant="primary" onClick={onPgnClick} disabled={importing}>
                Add PGN
              </Button>
            </div>
          </>
        ) : null}

        {tab === 'photo' ? (
          <>
            <p className="cm-import-help">
              Photograph a paper annotation sheet. The whole sheet, or one column at a time. We read
              the moves; anything the camera can’t make out, you fix on a board, one move at a time,
              until the whole game checks out. Opens the full-screen scanner.
            </p>
            <div className="cm-import-actions">
              <Button
                variant="primary"
                onClick={() => {
                  dispatch(setShowImportGames(false));
                  window.location.hash = '#/annotation';
                }}
              >
                <FontAwesomeIcon icon={faCamera} className="me-2" />
                Open the Annotation Scanner
              </Button>
            </div>
          </>
        ) : null}
      </Modal.Body>
      <Modal.Footer>
        <BarLoader color="var(--sc-accent)" loading={importing} width="100%" />
      </Modal.Footer>
    </Modal>
  );
}
